import { ApiError } from '../types/subscription';
import { getJwtFromCookie } from '../utils/auth';
import { getSubscriptionUrl } from '../config/api';

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
  timeout?: number;
  signal?: AbortSignal;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  status?: number;
}

export class ApiService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || '';
  }

  private getAuthHeaders(): Record<string, string> {
    const jwt = getJwtFromCookie();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    console.log('JWT from cookie:', jwt ? 'present' : 'missing');

    if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }

    return headers;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    try {
      const {
        method = 'GET',
        headers = {},
        body,
        credentials = 'include',
        timeout = 120000, // Default 2 minutes timeout
        signal
      } = options;

      const finalHeaders = {
        ...this.getAuthHeaders(),
        ...headers,
      };

      console.log('Final request headers:', finalHeaders);
      console.log('Request URL:', endpoint);

      // Create AbortController for timeout handling
      const controller = new AbortController();
      let timeoutId: number | undefined;

      // Use provided signal or create timeout signal
      let finalSignal = signal;
      if (!signal && timeout > 0) {
        timeoutId = window.setTimeout(() => {
          controller.abort();
        }, timeout);
        finalSignal = controller.signal;
      }

      const requestOptions: RequestInit = {
        method,
        headers: finalHeaders,
        credentials,
        signal: finalSignal,
      };

      if (body) {
        requestOptions.body = JSON.stringify(body);
      }

      const response = await fetch(endpoint, requestOptions);
      
      // Clear timeout if request completed
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        // Handle subscription errors specifically
        if (response.status === 403 && responseData.error === 'Subscription required') {
          console.log('[API] Subscription error detected:', responseData);
          throw new ApiError(
            responseData.message || 'Subscription required',
            403,
            responseData.subscriptionUrl || getSubscriptionUrl(),
            responseData.subscriptionStatus
          );
        }

        return {
          success: false,
          error: responseData.message || `HTTP ${response.status}`,
          message: responseData.message || 'Request failed',
          status: response.status
        };
      }

      return {
        success: true,
        data: responseData,
        message: responseData.message
      };
    } catch (error) {
      // Re-throw ApiError instances
      if (error instanceof ApiError) {
        throw error;
      }

      console.error('API request failed:', error);
      
      // Handle timeout errors specifically
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Request timeout - the server took too long to respond',
          message: 'Request failed due to timeout'
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
        message: 'Request failed due to network error'
      };
    }
  }

  // Generic API methods
  async get<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, { ...options, method: 'POST', body: data });
  }

  async put<T>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, { ...options, method: 'PUT', body: data });
  }

  async delete<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, { ...options, method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(`${this.baseUrl}${endpoint}`, { ...options, method: 'PATCH', body: data });
  }

  // Helper method for handling responses with subscription error checking
  static async handleResponse(response: Response, endpoint: string): Promise<any> {
    if (response.ok) {
      return await response.json();
    }
    
    // Handle subscription errors
    if (response.status === 403) {
      const errorData = await response.json();
      if (errorData.error === 'Subscription required') {
        console.log(`[API] Subscription error in ${endpoint}:`, errorData);
        throw new ApiError(
          errorData.message || 'Subscription required',
          403,
          errorData.subscriptionUrl || getSubscriptionUrl(),
          errorData.subscriptionStatus
        );
      }
    }
    
    const errorData = await response.json();
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }
}

// Export a default instance
export const apiService = new ApiService();

// Export the class for custom instances
export default ApiService; 